import React, { useState, useEffect } from "react";
import { pharmacyQuestions as defaultQuestions } from "../questions/pharmacy";
import { translations } from "../translations";
import { fetchQuestions, fetchResults, saveQuestions } from "../services/api";
import { resolveTypingPassages } from "../utils/typingTest";
import {
  isAdminSessionActive,
  setAdminMode,
  setAdminSession,
} from "../utils/adminSession";
import AdminLogin from "./admin/AdminLogin";
import AdminLayout from "./admin/AdminLayout";
import AdminDashboard from "./admin/AdminDashboard";
import AdminQuestions from "./admin/AdminQuestions";
import AdminResultsTable from "./admin/AdminResultsTable";
import ParticipantDetail from "./admin/ParticipantDetail";

const LOCAL_QUESTIONS_KEY = "hevmed_questions_fallback";
const LOCAL_CONFIG_KEY = "hevmed_exam_config_fallback";
const LOCAL_TYPING_PASSAGES_KEY = "hevmed_typing_passages_fallback";

const Skeleton = () => (
  <div className="admin-skeleton-grid">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="admin-skeleton-card">
        <div className="skeleton-line title" />
        <div className="skeleton-line text" />
      </div>
    ))}
  </div>
);

const AdminPanel = ({ onExit, lang, currentQuestions, onUpdateQuestions }) => {
  const t = translations[lang];
  const [isAuthenticated, setIsAuthenticated] = useState(isAdminSessionActive);
  const [questions, setQuestions] = useState(currentQuestions || []);
  const [questionLimit, setQuestionLimit] = useState("10");
  const [typingPassages, setTypingPassages] = useState([]);
  const [results, setResults] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedResult, setSelectedResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [fetchedData, fetchedResults] = await Promise.all([
          fetchQuestions(),
          fetchResults(),
        ]);

        setQuestions(fetchedData.questions);
        setQuestionLimit(String(fetchedData.questionLimit));
        setTypingPassages(resolveTypingPassages(fetchedData.typingPassages));
        setResults(Array.isArray(fetchedResults) ? fetchedResults : []);
      } catch (err) {
        console.error("Failed to load data:", err);
        const savedLocalQuestions = localStorage.getItem(LOCAL_QUESTIONS_KEY);
        setQuestions(
          savedLocalQuestions
            ? JSON.parse(savedLocalQuestions)
            : (currentQuestions?.length ? currentQuestions : defaultQuestions)
        );
        const savedLimit = localStorage.getItem(LOCAL_CONFIG_KEY);
        setQuestionLimit(savedLimit || "10");
        const savedPassages = localStorage.getItem(LOCAL_TYPING_PASSAGES_KEY);
        setTypingPassages(resolveTypingPassages(savedPassages ? JSON.parse(savedPassages) : []));
        setResults([]);
        setError(t.dbUnreachable);
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      loadInitialData();
    }
  }, [currentQuestions, isAuthenticated, t.dbUnreachable]);

  const handleLogin = ({ email, password }) => {
    if (
      email === "heavenmedicalconsumercare@gmail.com" &&
      password === "Heaven@235"
    ) {
      setAdminSession(true);
      setAdminMode(true);
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const handleLogout = () => {
    setAdminSession(false);
    setIsAuthenticated(false);
    onExit();
  };

  const persistQuestions = async (newQs, limit, passages) => {
    setIsSaving(true);
    setError(null);

    try {
      const saved = await saveQuestions(newQs, limit, passages);
      setQuestions(saved.questions);
      setQuestionLimit(String(saved.questionLimit));
      setTypingPassages(saved.typingPassages || []);
      localStorage.setItem(LOCAL_QUESTIONS_KEY, JSON.stringify(saved.questions));
      localStorage.setItem(LOCAL_CONFIG_KEY, String(saved.questionLimit));
      localStorage.setItem(LOCAL_TYPING_PASSAGES_KEY, JSON.stringify(saved.typingPassages || []));
      if (onUpdateQuestions) {
        onUpdateQuestions(saved.questions, saved.questionLimit, saved.typingPassages);
      }
      alert(t.questionsSaved);
    } catch (err) {
      console.error("Failed to save questions:", err);
      localStorage.setItem(LOCAL_QUESTIONS_KEY, JSON.stringify(newQs));
      localStorage.setItem(LOCAL_CONFIG_KEY, String(limit));
      localStorage.setItem(LOCAL_TYPING_PASSAGES_KEY, JSON.stringify(passages || []));
      setQuestions(newQs);
      setQuestionLimit(String(limit));
      setTypingPassages(passages || []);
      if (onUpdateQuestions) onUpdateQuestions(newQs, limit, passages);
      setError(t.cloudSaveFailed);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <AdminLogin
        t={t}
        onLogin={handleLogin}
        onExit={onExit}
      />
    );
  }

  if (selectedResult) {
    return (
      <AdminLayout
        t={t}
        activeTab="results"
        onTabChange={(tab) => {
          setActiveTab(tab);
          setSelectedResult(null);
          setError(null);
        }}
        onExit={handleLogout}
        error={error}
        onRetry={() => window.location.reload()}
        pageTitle={t.participantDetails}
        pageSubtitle={selectedResult.candidate?.fullName}
      >
        <ParticipantDetail
          result={selectedResult}
          questions={questions}
          t={t}
          onBack={() => setSelectedResult(null)}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      t={t}
      activeTab={activeTab}
      onTabChange={(tab) => { setActiveTab(tab); setError(null); }}
      onExit={handleLogout}
      error={error}
      onRetry={() => window.location.reload()}
    >
      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          {activeTab === "dashboard" && (
            <AdminDashboard results={results} t={t} />
          )}

          {activeTab === "questions" && (
            <AdminQuestions
              questions={questions}
              setQuestions={setQuestions}
              questionLimit={questionLimit}
              onQuestionLimitChange={setQuestionLimit}
              typingPassages={typingPassages}
              onTypingPassagesChange={setTypingPassages}
              onSave={persistQuestions}
              isSaving={isSaving}
              t={t}
            />
          )}

          {activeTab === "results" && (
            <AdminResultsTable
              results={results}
              questions={questions}
              t={t}
              onSelectResult={setSelectedResult}
            />
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminPanel;
